
import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { ReferralTemplate } from '@/utils/referral/types';

export const useReferralTemplates = () => {
  const [templates, setTemplates] = useState<ReferralTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  
  // Load templates
  const loadTemplates = async () => {
    setIsLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolver => setTimeout(resolver, 800));
      
      // Mock templates
      const mockTemplates: ReferralTemplate[] = [
        {
          id: 'template-1',
          name: 'Default Email Template',
          subject: 'Join me on this awesome platform!',
          body: 'Hi {name}, I thought you might be interested in this platform. Use my referral link: {referralLink}',
          type: 'email',
          isDefault: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          variables: ['name', 'referralLink']
        },
        {
          id: 'template-2',
          name: 'SMS Template',
          subject: 'N/A',
          body: 'Hey {name}! Check out this link I sent you: {referralLink}',
          type: 'sms',
          isDefault: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          variables: ['name', 'referralLink']
        }
      ];
      
      setTemplates(mockTemplates);
    } catch (error) {
      console.error('Error loading templates:', error);
      toast({
        title: 'Error loading templates',
        description: 'Failed to load referral templates',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Load templates on mount
  useEffect(() => {
    loadTemplates();
  }, []);
  
  // Add or update a template
  const saveTemplate = async (templateData: Partial<ReferralTemplate>) => {
    try {
      // Check if this is a new template or an update
      const isNew = !templateData.id;
      
      // Create a new template object
      const updatedTemplate: ReferralTemplate = {
        id: templateData.id || `template-${Date.now()}`,
        name: templateData.name || 'Untitled Template',
        subject: templateData.subject || '',
        body: templateData.body || '',
        type: templateData.type || 'email',
        isDefault: templateData.isDefault || false,
        createdAt: isNew ? new Date().toISOString() : (templateData.createdAt || new Date().toISOString()),
        updatedAt: new Date().toISOString(),
        variables: templateData.variables || []
      };
      
      // Update state
      if (isNew) {
        setTemplates(prev => [...prev, updatedTemplate]);
      } else {
        setTemplates(prev => 
          prev.map(t => t.id === updatedTemplate.id ? updatedTemplate : t)
        );
      }
      
      toast({
        title: isNew ? 'Template created' : 'Template updated',
        description: `Template "${updatedTemplate.name}" has been ${isNew ? 'created' : 'updated'}.`,
      });
      
      return updatedTemplate;
    } catch (error) {
      console.error('Error saving template:', error);
      toast({
        title: 'Error saving template',
        description: 'Failed to save referral template',
        variant: 'destructive',
      });
      return null;
    }
  };
  
  // Delete a template
  const deleteTemplate = async (templateId: string) => {
    try {
      // Remove template from state
      setTemplates(prev => prev.filter(t => t.id !== templateId));
      
      toast({
        title: 'Template deleted',
        description: 'Referral template has been deleted.',
      });
      
      return true;
    } catch (error) {
      console.error('Error deleting template:', error);
      toast({
        title: 'Error deleting template',
        description: 'Failed to delete referral template',
        variant: 'destructive',
      });
      return false;
    }
  };
  
  return {
    templates,
    isLoading,
    loadTemplates,
    saveTemplate,
    deleteTemplate
  };
};
